import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import CustomDataTable from "components/customs/table";
import { defaultTableOption, rowPerPage } from "constants/table";
import { showDeleteModal, Toast } from "utility/helpers";
import { genericGet } from "services/services.general";
import styled from "styled-components";
import { HeaderText } from "assets/js/Styles";
import { ADD_ACTION, EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import { TWENTIETH_CONGRESS } from "utility/constants";
import useRegions from "hooks/useRegions";
import usePartylists from "hooks/usePartylists";
import useDistricts from "hooks/useDistricts";
import useCommittees from "hooks/useCommittees";
import usePositions from "hooks/usePositions";
import committeeColumns from "./columns";
import CommitteeModal from "./committeeModal";
import CommitteeFilters from "./CommitteeFilters";
import { generateCommitteePDF, generateCommitteeDOCx } from "./exportUtility";

const TableSection = styled.div`
  margin-top: 16px;
`;

const HeaderSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const Main = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [selectedHouseMember, setSelectedHouseMember] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedAffiliation, setSelectedAffiliation] = useState(null);
  const [houseMembers, setHouseMembers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showChairpersonOnly, setShowChairpersonOnly] = useState(false);
  const [modalMode, setModalMode] = useState(ADD_ACTION);
  const [modalInitialData, setModalInitialData] = useState(null);

  const { options: regions } = useRegions();
  const { options: affiliations } = usePartylists();
  const { options: districts } = useDistricts();
  const { options: committees } = useCommittees();
  const { options: positions } = usePositions();

  const navigate = useNavigate();

  const selectedCongress =
    useSelector((state) => state.congress?.selectedCongress) ||
    TWENTIETH_CONGRESS;

  const onChangePage = (page) => {
    setTableOption((prev) => ({
      ...prev,
      page,
      offset: prev.currentRowsPerPage * (page - 1),
    }));
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption((prev) => ({
      ...prev,
      currentRowsPerPage,
      page: currentPage,
      offset: (currentPage - 1) * currentRowsPerPage,
    }));
  };

  const onSort = (column, sortDirection) => {
    setTableOption((prev) => ({
      ...prev,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    }));
  };

  const fetchDataTable = () => {
    setTableOption((prev) => ({ ...prev, isLoading: true }));

    genericGet({
      url: "/membership-committee",
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        committeeId: selectedCommittee?.value,
        congressmanId: selectedHouseMember?.value,
        regionId: selectedRegion?.value,
        partyId: selectedAffiliation?.value,
        positionId: showChairpersonOnly ? 3 : undefined,
      },
      callback: (data) => {
        setTableOption((prev) => ({
          ...prev,
          data: data?.rows || [],
          totalRows: data?.count || 0,
          isLoading: false,
        }));
      },
    });
  };

  const fetchFilterOptions = async () => {
    try {
      const houseMembersData = await new Promise((resolve) => {
        genericGet({
          url: "/congressman",
          callback: (data) => resolve(data || []),
        });
      });

      setHouseMembers(
        (houseMembersData || []).map((item) => ({
          value: item.id,
          label: item.name || item.fullName,
          data: item, // Include full data object for auto-filling
        }))
      );
    } catch (error) {
      Toast({
        type: 4,
        content: "Failed to load filter options. Please refresh the page.",
      });
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);

    if (!isModalOpen) {
      // Reset to create mode when closing
      setModalMode(ADD_ACTION);
      setModalInitialData(null);
    }
  };

  const handleEditAction = (row) => {
    setModalInitialData(row);
    setModalMode(EDIT_ACTION);
    setIsModalOpen(true);
  };

  const handleViewAction = (row) => {
    setModalInitialData(row);
    setModalMode(VIEW_ACTION);
    setIsModalOpen(true);
  };

  const handleDeleteAction = (row) => {
    showDeleteModal({
      title: "this committee assignment",
      url: `/membership-committee/${row.id}`,
      callBack: () => {
        fetchDataTable();
      },
    });
  };

  const handleExport = (format) => {
    genericGet({
      url: "/membership-committee",
      params: {
        limit: 10000,
        offset: 0,
        search: tableOption.searchValue || "",
        column: tableOption.sort.field || "",
        order: tableOption.sort.direction || "",
        committeeId: selectedCommittee?.value,
        congressmanId: selectedHouseMember?.value,
        regionId: selectedRegion?.value,
        partyId: selectedAffiliation?.value,
        positionId: showChairpersonOnly ? 3 : undefined,
        compiledExport: true,
      },
      callback: async (responseData) => {
        if (!responseData) {
          Toast({
            type: 3,
            content: "No data available to export.",
          });

          return;
        }

        try {
          if (format === "pdf") {
            generateCommitteePDF(responseData, selectedCongress);
          } else {
            await generateCommitteeDOCx(responseData, selectedCongress);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Export error:", error);

          Toast({
            type: 4,
            content: `Failed to export ${format.toUpperCase()} file.`,
          });
        }
      },
    });
  };

  const columns = useMemo(
    () =>
      committeeColumns({
        navigate,
        handleDeleteAction,
        handleEditAction,
        handleViewAction,
      }),
    [navigate, handleDeleteAction, handleEditAction, handleViewAction]
  );

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchDataTable();
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.field,
    tableOption.sort.direction,
    selectedCommittee?.value,
    selectedHouseMember?.value,
    selectedRegion?.value,
    selectedAffiliation?.value,
    showChairpersonOnly,
  ]);

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <HeaderSection>
            <HeaderText>{"Committee Management System"}</HeaderText>
          </HeaderSection>

          <CommitteeFilters
            committees={committees}
            selectedCommittee={selectedCommittee}
            setSelectedCommittee={setSelectedCommittee}
            setTableOption={setTableOption}
            houseMembers={houseMembers}
            selectedHouseMember={selectedHouseMember}
            setSelectedHouseMember={setSelectedHouseMember}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            regions={regions}
            selectedAffiliation={selectedAffiliation}
            affiliations={affiliations}
            setSelectedAffiliation={setSelectedAffiliation}
            toggleModal={toggleModal}
            showChairpersonOnly={showChairpersonOnly}
            setShowChairpersonOnly={setShowChairpersonOnly}
            tableOption={tableOption}
            onExport={handleExport}
          />

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
              defaultSortField={tableOption.sort.field}
              defaultSortAsc={tableOption.sort.direction}
              height="calc(100vh - 400px)"
            />
          </TableSection>
        </CardContainer>
      </CardSection>

      <CommitteeModal
        isOpen={isModalOpen}
        handleClose={toggleModal}
        houseMembers={houseMembers}
        regions={regions}
        districts={districts}
        affiliations={affiliations}
        committees={committees}
        positions={positions}
        fetchDataTable={fetchDataTable}
        mode={modalMode}
        initialData={modalInitialData}
      />
    </Wrapper>
  );
};

export default Main;
